import { Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ClientAuthProvider, useClientAuth } from './contexts/ClientAuthContext';
import AdminLayout from './components/AdminLayout';
import ClientPortalLayout from './components/ClientPortalLayout';
import LoginPage from './pages/LoginPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import DashboardPage from './pages/DashboardPage';
import ClientsPage from './pages/ClientsPage';
import ClientDetailPage from './pages/ClientDetailPage';
import StorageManagerPage from './pages/StorageManagerPage';
import AdminSettingsPage from './pages/AdminSettingsPage';
import OutreachReviewPage from './pages/outreach/OutreachReviewPage';
import OutreachDashboardPage from './pages/outreach/OutreachDashboardPage';
import OutreachProspectsPage from './pages/outreach/OutreachProspectsPage';
import OutreachCRMPage from './pages/outreach/OutreachCRMPage';
import OutreachSettingsPage from './pages/outreach/OutreachSettingsPage';
import AdminOnboardingPage from './pages/AdminOnboardingPage';
import AdminOnboardingDetailPage from './pages/AdminOnboardingDetailPage';
import PortalPage from './pages/PortalPage';
import ClientLoginPage from './pages/portal/ClientLoginPage';
import ClientRegisterPage from './pages/portal/ClientRegisterPage';
import ClientVerifyPage from './pages/portal/ClientVerifyPage';
import ClientVerifyEmailPage from './pages/portal/ClientVerifyEmailPage';
import ClientDashboardPage from './pages/portal/ClientDashboardPage';
import AgreementPage from './pages/portal/AgreementPage';
import PaymentPage from './pages/portal/PaymentPage';
import OnboardingPage from './pages/portal/OnboardingPage';
import ClientNotificationsPage from './pages/portal/ClientNotificationsPage';
import ClientSettingsPage from './pages/portal/ClientSettingsPage';
import ClientFeedbackPage from './pages/portal/ClientFeedbackPage';
import ManageDashboardPage from './pages/portal/manage/ManageDashboardPage';
import ManageBookingsPage from './pages/portal/manage/ManageBookingsPage';
import ManageCalendarPage from './pages/portal/manage/ManageCalendarPage';
import ManagePricingPage from './pages/portal/manage/ManagePricingPage';
import ManageMessagesPage from './pages/portal/manage/ManageMessagesPage';
import ManageSettingsPage from './pages/portal/manage/ManageSettingsPage';
import ManagePropertiesPage from './pages/portal/manage/ManagePropertiesPage';
import ManagePayoutsPage from './pages/portal/manage/ManagePayoutsPage';
import ManageReviewsPage from './pages/portal/manage/ManageReviewsPage';
import ManageGuestAppPage from './pages/portal/manage/ManageGuestAppPage';
import ManageTimelinePage from './pages/portal/manage/ManageTimelinePage';
import ManageOwnerReportsPage from './pages/portal/manage/ManageOwnerReportsPage';
import ManageAutoMessagesPage from './pages/portal/manage/ManageAutoMessagesPage';
import ManageSesPage from './pages/portal/manage/ManageSesPage';
import ManageGuestsPage from './pages/portal/manage/ManageGuestsPage';
import ManageNewsletterPage from './pages/portal/manage/ManageNewsletterPage';
import ManageOwnersPage from './pages/portal/manage/ManageOwnersPage';
import AnalyticsPage from './pages/portal/AnalyticsPage';
import CrispChat from './components/CrispChat';
import { lazy, Suspense, type ReactNode } from 'react';
import { getTenantSubdomain } from './lib/tenant-host';
import { EMAIL_EDITOR_ENABLED } from './config/features';

// Lazy-load the tenant-site and guest-facing pages so they're split out of the
// main bundle and only loaded when their routes are visited.
const AdminTenantSitePage = lazy(() => import('./pages/AdminTenantSitePage'));
const MySitePreviewPage = lazy(() => import('./pages/portal/MySitePreviewPage'));
const PublicSitePage = lazy(() => import('./pages/public/PublicSitePage'));
const GuestPortalPage = lazy(() => import('./pages/public/GuestPortalPage'));
const GuestRegistrationPage = lazy(() => import('./pages/public/GuestRegistrationPage'));
const SubmitReviewPage = lazy(() => import('./pages/public/SubmitReviewPage'));
const BookingConfirmPage = lazy(() => import('./pages/public/BookingConfirmPage'));
const BookingCancelledPage = lazy(() => import('./pages/public/BookingCancelledPage'));
const BookingBalancePage = lazy(() => import('./pages/public/BookingBalancePage'));
const BookingDepositPage = lazy(() => import('./pages/public/BookingDepositPage'));

function ProtectedRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-teal-50">
        <div className="animate-pulse text-teal-500 text-lg font-medium">Cargando...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function ProtectedClientRoute({ children }: { children: ReactNode }) {
  const { user, loading } = useClientAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-pulse text-teal-500 text-lg font-medium">Cargando...</div>
      </div>
    );
  }

  if (!user) return <Navigate to="/portal/login" replace />;
  return <>{children}</>;
}

export default function App() {
  // Tenant subdomain (e.g. mialojamiento.kanarix.es) → render ONLY that tenant's public
  // site, no app chrome/auth. The slug comes from the host, not the path.
  const tenantSlug = getTenantSubdomain();
  if (tenantSlug) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
        <PublicSitePage slugOverride={tenantSlug} />
      </Suspense>
    );
  }

  return (
    <>
    <CrispChat />
    <Routes>
      {/* Public tenant websites (no auth, published content only) */}
      <Route
        path="/site/:slug"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <PublicSitePage />
          </Suspense>
        }
      />

      {/* Public guest portal (no auth, tokenized link the owner shares with guests) */}
      <Route
        path="/guest/:token"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <GuestPortalPage />
          </Suspense>
        }
      />

      {/* Public guest registration — parte de viajeros (no auth, gated by booking
          code in the URL + the booking email the guest enters) */}
      <Route
        path="/registro/:bookingCode"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <GuestRegistrationPage />
          </Suspense>
        }
      />

      {/* Public direct-review submission — no auth, gated by booking code in the
          URL + the booking email the guest enters. Linked from the post-stay email. */}
      <Route
        path="/opinar/:bookingCode"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <SubmitReviewPage />
          </Suspense>
        }
      />

      {/* Public post-checkout pages (no auth — Stripe return URLs) */}
      <Route
        path="/booking/confirm"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <BookingConfirmPage />
          </Suspense>
        }
      />
      <Route
        path="/booking/cancelled"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <BookingCancelledPage />
          </Suspense>
        }
      />
      <Route
        path="/booking/balance"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <BookingBalancePage />
          </Suspense>
        }
      />
      <Route
        path="/booking/deposit"
        element={
          <Suspense fallback={<div className="min-h-screen flex items-center justify-center text-slate-400 text-sm">Cargando…</div>}>
            <BookingDepositPage />
          </Suspense>
        }
      />

      {/* Admin auth */}
      <Route path="/login" element={<AuthProvider><LoginPage /></AuthProvider>} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />

      {/* Public portal (magic-link read-only) */}
      <Route path="/portal/p/:token" element={<PortalPage />} />

      {/* Client portal auth */}
      <Route path="/portal/login" element={<ClientAuthProvider><ClientLoginPage /></ClientAuthProvider>} />
      <Route path="/portal/register" element={<ClientAuthProvider><ClientRegisterPage /></ClientAuthProvider>} />
      <Route path="/portal/verify" element={<ClientAuthProvider><ClientVerifyPage /></ClientAuthProvider>} />
      <Route path="/portal/verify-email" element={<ClientAuthProvider><ClientVerifyEmailPage /></ClientAuthProvider>} />

      {/* Client portal (authenticated) */}
      <Route
        path="/portal/*"
        element={
          <ClientAuthProvider>
            <ProtectedClientRoute>
              <ClientPortalLayout>
                <Routes>
                  <Route path="/dashboard" element={<ClientDashboardPage />} />
                  <Route path="/agreement" element={<AgreementPage />} />
                  <Route path="/payment" element={<PaymentPage />} />
                  <Route path="/onboarding" element={<OnboardingPage />} />
                  {/* Management plane (post-onboarding owner dashboard) */}
                  <Route path="/manage/dashboard" element={<ManageDashboardPage />} />
                  <Route path="/manage/analytics" element={<AnalyticsPage />} />
                  <Route path="/manage/bookings" element={<ManageBookingsPage />} />
                  <Route path="/manage/calendar" element={<ManageCalendarPage />} />
                  <Route path="/manage/pricing" element={<ManagePricingPage />} />
                  <Route path="/manage/messages" element={<ManageMessagesPage />} />
                  <Route path="/manage/properties" element={<ManagePropertiesPage />} />
                  <Route path="/manage/payouts" element={<ManagePayoutsPage />} />
                  <Route path="/manage/reviews" element={<ManageReviewsPage />} />
                  <Route path="/manage/guest-app" element={<ManageGuestAppPage />} />
                  <Route path="/manage/timeline" element={<ManageTimelinePage />} />
                  <Route path="/manage/reports" element={<ManageOwnerReportsPage />} />
                  <Route path="/manage/auto-messages" element={<ManageAutoMessagesPage />} />
                  <Route path="/manage/ses" element={<ManageSesPage />} />
                  <Route path="/manage/guests" element={<ManageGuestsPage />} />
                  <Route path="/manage/owners" element={<ManageOwnersPage />} />
                  {EMAIL_EDITOR_ENABLED && <Route path="/manage/newsletter" element={<ManageNewsletterPage />} />}
                  <Route path="/manage/settings" element={<ManageSettingsPage />} />
                  <Route path="/notifications" element={<ClientNotificationsPage />} />
                  <Route path="/settings" element={<ClientSettingsPage />} />
                  <Route
                    path="/my-site"
                    element={
                      <Suspense fallback={<div className="p-8 text-slate-400 text-sm">Cargando…</div>}>
                        <MySitePreviewPage />
                      </Suspense>
                    }
                  />
                  <Route path="/feedback" element={<ClientFeedbackPage />} />
                  <Route path="*" element={<Navigate to="/portal/dashboard" replace />} />
                </Routes>
              </ClientPortalLayout>
            </ProtectedClientRoute>
          </ClientAuthProvider>
        }
      />

      {/* Admin panel */}
      <Route
        path="/*"
        element={
          <AuthProvider>
            <ProtectedRoute>
              <AdminLayout>
                <Routes>
                  <Route path="/" element={<DashboardPage />} />
                  <Route path="/clients" element={<ClientsPage />} />
                  <Route path="/clients/:id" element={<ClientDetailPage />} />
                  <Route
                    path="/tenant-site/:clientId"
                    element={
                      <Suspense fallback={<div className="p-8 text-slate-400 text-sm">Cargando editor…</div>}>
                        <AdminTenantSitePage />
                      </Suspense>
                    }
                  />
                  <Route path="/onboarding" element={<AdminOnboardingPage />} />
                  <Route path="/onboarding/:id" element={<AdminOnboardingDetailPage />} />
                  <Route path="/storage" element={<StorageManagerPage />} />
                  <Route path="/settings" element={<AdminSettingsPage />} />
                  <Route path="/outreach" element={<OutreachDashboardPage />} />
                  <Route path="/outreach/review" element={<OutreachReviewPage />} />
                  <Route path="/outreach/dashboard" element={<OutreachDashboardPage />} />
                  <Route path="/outreach/prospects" element={<OutreachProspectsPage />} />
                  <Route path="/outreach/crm" element={<OutreachCRMPage />} />
                  <Route path="/outreach/settings" element={<OutreachSettingsPage />} />
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              </AdminLayout>
            </ProtectedRoute>
          </AuthProvider>
        }
      />
    </Routes>
    </>
  );
}
